import { useState } from "react";
import { Link } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import { Button, Card, Input, Select, Spinner } from "@nutrayan/ui";
import { formatCurrency } from "@nutrayan/utils";
import { useDebouncedValue } from "@/lib/use-debounced-value";
import { listProductsForAdmin, type ProductStatus } from "@/lib/catalog-api";

// Admin product list (BLK-API-002): GET /admin/api/v1/catalog/products includes
// DRAFT/ARCHIVED products, with server-side status filter + free-text search.
const STATUSES: ProductStatus[] = ["DRAFT", "PUBLISHED", "ARCHIVED"];

export default function CatalogListPage() {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<ProductStatus | "">("");
  const search = useDebouncedValue(q, 300);

  const query = useQuery({
    queryKey: ["admin", "products", { status, search }],
    queryFn: () => listProductsForAdmin({ status, search, page: 0, size: 50 }),
    placeholderData: keepPreviousData,
  });

  const rows = query.data ?? [];

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-display-xl text-charcoal">Catalog</h1>
          <p className="text-body text-ink">All products — drafts, published, and archived.</p>
        </div>
        <Link to="/catalog/new">
          <Button size="md">+ New product</Button>
        </Link>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search products"
          aria-label="Search products"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-sm"
        />
        <Select
          aria-label="Filter by status"
          value={status}
          onChange={(e) => setStatus(e.target.value as ProductStatus | "")}
          className="w-44"
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s.toLowerCase()}
            </option>
          ))}
        </Select>
      </div>

      {query.isPending ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : query.isError ? (
        <Card>
          <p className="text-body text-terracotta">
            Couldn&apos;t load the catalog. Check API connectivity.
          </p>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <p className="text-body text-ink">No products match.</p>
        </Card>
      ) : (
        <Card className="p-0">
          <table className="text-body-sm w-full">
            <thead className="border-charcoal/10 text-caption text-muted border-b text-left">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Slug</th>
                <th className="px-4 py-2">Line</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2 text-right">From price</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-charcoal/5 hover:bg-sand/40 border-b">
                  <td className="text-charcoal px-4 py-2">{p.name ?? "—"}</td>
                  <td className="text-ink px-4 py-2 font-mono">{p.slug ?? "—"}</td>
                  <td className="text-ink px-4 py-2">{(p.line ?? "—").toLowerCase()}</td>
                  <td className="text-ink px-4 py-2">{(p.status ?? "—").toLowerCase()}</td>
                  <td className="price text-charcoal px-4 py-2 text-right">
                    {typeof p.fromPricePaise === "number" ? formatCurrency(p.fromPricePaise) : "—"}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {p.slug ? (
                      <Link
                        to={`/catalog/${p.slug}/edit`}
                        className="text-forest inline-flex items-center gap-1 font-medium hover:underline"
                      >
                        <Pencil size={13} aria-hidden /> Edit
                      </Link>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
